import { ChangeEvent, useMemo, useState } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { ResourceCategoryTypes } from "@enums/resource-category-types";
import {
    AXNUITheme,
    AXNUIThemeGeneral
} from "@iddataweb/idw-common-ui/types";
import { SelectOption } from "@idw-types/common";
import { SelectFormGroup } from "../../../../../common/select-form-group/select-form-group";
import { ColorPickerFormGroup } from "../../../../../common/color-picker-form-group/color-picker-form-group";
import { FileInputFormGroup } from "../../../../../common/file-input-form-group/file-input-form-group";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";

type ThemingLayoutBackgroundSectionProps = {
    layout: LayoutTypes;
    labelWidth: LabelWidthTypes;
    isDisabled: boolean;
    axnuiTheme: AXNUITheme;
    inheritedAXNUITheme: AXNUITheme;
    pageLevel: PageLevelTypes;
    uploadFileToS3: (file: File, category: string) => Promise<string>;
    updateAXNUIThemeGeneralFieldValue: (key: keyof AXNUIThemeGeneral, value: string) => void;
};

export const ThemingLayoutBackgroundSection = (props: ThemingLayoutBackgroundSectionProps) => {
    const {
        layout,
        labelWidth,
        isDisabled,
        axnuiTheme,
        inheritedAXNUITheme,
        pageLevel,
        uploadFileToS3,
        updateAXNUIThemeGeneralFieldValue
    } = props;

    const [backgroundImageUploadInProgress, setBackgroundImageUploadInProgress] = useState(false);

    const handleBackgroundColorChange = (newValue: string) => {
        updateAXNUIThemeGeneralFieldValue("outerBackgroundColor", newValue);
    };

    const handleResetBackgroundColor = () => {
        updateAXNUIThemeGeneralFieldValue("outerBackgroundColor", inheritedAXNUITheme.general.outerBackgroundColor);
    };

    const handleBackgroundImageChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files.length > 0) {
            const file = files[0];
            //upload to s3
            setBackgroundImageUploadInProgress(true);
            const uploadResult = await uploadFileToS3(file, ResourceCategoryTypes.BACKGROUND_IMAGES);
            if (uploadResult) {
                updateAXNUIThemeGeneralFieldValue("outerBackgroundImageURL", uploadResult);
            }
            setBackgroundImageUploadInProgress(false);
        }
    };

    const handleResetBackgroundImage = () => {
        updateAXNUIThemeGeneralFieldValue("outerBackgroundImageURL", inheritedAXNUITheme.general.outerBackgroundImageURL || "");
    };

    const handleBackgroundImagePositionChange = (newValue: SelectOption) => {
        updateAXNUIThemeGeneralFieldValue("outerBackgroundPosition", newValue.label);
    };

    const handleResetBackgroundImagePosition = () => {
        updateAXNUIThemeGeneralFieldValue("outerBackgroundPosition", inheritedAXNUITheme.general.outerBackgroundPosition);
    };

    const handleBackgroundImageSizeChange = (newValue: SelectOption) => {
        updateAXNUIThemeGeneralFieldValue("outerBackgroundSize", newValue.label);
    };

    const handleResetBackgroundImageSize = () => {
        updateAXNUIThemeGeneralFieldValue("outerBackgroundSize", inheritedAXNUITheme.general.outerBackgroundSize);
    };


    const handleBackgroundImageRepeatChange = (newValue: SelectOption) => {
        updateAXNUIThemeGeneralFieldValue("outerBackgroundRepeat", newValue.label);
    };

    const handleResetBackgroundImageRepeat = () => {
        updateAXNUIThemeGeneralFieldValue("outerBackgroundRepeat", inheritedAXNUITheme.general.outerBackgroundRepeat);
    };

    const handleBackgroundImageAttachmentChange = (newValue: SelectOption) => {
        updateAXNUIThemeGeneralFieldValue("outerBackgroundAttachment", newValue.label);
    };

    const handleResetBackgroundImageAttachment = () => {
        updateAXNUIThemeGeneralFieldValue("outerBackgroundAttachment", inheritedAXNUITheme.general.outerBackgroundAttachment);
    };

    const backgroundImagePositionOptions = useMemo(() => {
        return [
            { label: "center center", value: "center center" },
            { label: "center bottom", value: "center bottom" },
            { label: "center top", value: "center top" },
            { label: "left center", value: "left center" },
            { label: "left bottom", value: "left bottom" },
            { label: "left top", value: "left top" },
            { label: "right center", value: "right center" },
            { label: "right bottom", value: "right bottom" },
            { label: "right top", value: "right top" }
        ];
    }, []);

    const backgroundImageSizeOptions = useMemo(() => {
        return [
            { label: "cover", value: "cover" },
            { label: "auto", value: "auto" },
            { label: "contain", value: "contain" }
        ];
    }, []);

    const backgroundImageRepeatOptions = useMemo(() => {
        return [
            { label: "no-repeat", value: "no-repeat" },
            { label: "repeat", value: "repeat" }
        ];
    }, []);

    const backgroundImageAttachmentOptions = useMemo(() => {
        return [
            { label: "fixed", value: "fixed" },
            { label: "local", value: "local" },
            { label: "scroll", value: "scroll" }
        ];
    }, []);

    const selectedBackgroundImagePositionOption = useMemo(() => {
        if (axnuiTheme.general.outerBackgroundPosition) {
            return backgroundImagePositionOptions.find(po => po.label === axnuiTheme.general.outerBackgroundPosition);
        }
        return null;
    }, [axnuiTheme.general.outerBackgroundPosition]);

    const selectedBackgroundImageSizeOption = useMemo(() => {
        if (axnuiTheme.general.outerBackgroundSize) {
            return backgroundImageSizeOptions.find(so => so.label === axnuiTheme.general.outerBackgroundSize);
        }
        return null;
    }, [axnuiTheme.general.outerBackgroundSize]);

    const selectedBackgroundImageRepeatOption = useMemo(() => {
        if (axnuiTheme.general.outerBackgroundRepeat) {
            return backgroundImageRepeatOptions.find(ro => ro.label === axnuiTheme.general.outerBackgroundRepeat);
        }
        return null;
    }, [axnuiTheme.general.outerBackgroundRepeat]);

    const selectedBackgroundImageAttachmentOption = useMemo(() => {
        if (axnuiTheme.general.outerBackgroundAttachment) {
            return backgroundImageAttachmentOptions.find(ao => ao.label === axnuiTheme.general.outerBackgroundAttachment);
        }
        return null;
    }, [axnuiTheme.general.outerBackgroundAttachment]);

    return (
        <div>
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUITheme.general.outerBackgroundColor}
                currentThemingPropertyValue={axnuiTheme.general.outerBackgroundColor}
                pageLevel={pageLevel}
            >
                <ColorPickerFormGroup
                    controlId={"theming-layout-background-color-picker"}
                    automationId={"theming-layout-background-color-picker"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.background.inputs.backgroundColor.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.background.inputs.backgroundColor.label}
                    disabled={isDisabled}
                    isRequired={true}
                    layout={layout}
                    labelWidth={labelWidth}
                    value={axnuiTheme.general.outerBackgroundColor || ""}
                    onChange={handleBackgroundColorChange}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.background.inputs.backgroundColor.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetBackgroundColor}
                />
            </ThemingFormGroupWrapper>

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUITheme.general.outerBackgroundImageURL}
                currentThemingPropertyValue={axnuiTheme.general.outerBackgroundImageURL}
                pageLevel={pageLevel}
            >
                <FileInputFormGroup
                    controlId={"theming-layout-background-image-input"}
                    automationId={"theming-layout-background-image-input"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.background.inputs.backgroundImage.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.background.inputs.backgroundImage.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleBackgroundImageChange}
                    value={axnuiTheme.general.outerBackgroundImageURL || ""}
                    accept={"image/png, image/gif, image/jpeg, image/jpg"}
                    disabled={isDisabled}
                    uploadInProgress={backgroundImageUploadInProgress}
                    isRequired={false}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.background.inputs.backgroundImage.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetBackgroundImage}
                />
            </ThemingFormGroupWrapper>

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUITheme.general.outerBackgroundPosition}
                currentThemingPropertyValue={axnuiTheme.general.outerBackgroundPosition}
                pageLevel={pageLevel}
            >
                <SelectFormGroup
                    controlId={"theming-layout-background-image-postition-select-input"}
                    automationId={"theming-layout-background-image-position-select-input"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.background.inputs.imagePosition.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.background.inputs.imagePosition.label}
                    disabled={isDisabled}
                    isClearable={false}
                    isRequired={false}
                    isSearchable={false}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleBackgroundImagePositionChange}
                    options={backgroundImagePositionOptions}
                    selectedOption={selectedBackgroundImagePositionOption}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.background.inputs.imagePosition.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetBackgroundImagePosition}
                />
            </ThemingFormGroupWrapper>

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUITheme.general.outerBackgroundSize}
                currentThemingPropertyValue={axnuiTheme.general.outerBackgroundSize}
                pageLevel={pageLevel}
            >
                <SelectFormGroup
                    controlId={"theming-layout-background-image-size-select-input"}
                    automationId={"theming-layout-background-image-size-select-input"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.background.inputs.imageSize.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.background.inputs.imageSize.label}
                    disabled={isDisabled}
                    isClearable={false}
                    isRequired={false}
                    isSearchable={false}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleBackgroundImageSizeChange}
                    options={backgroundImageSizeOptions}
                    selectedOption={selectedBackgroundImageSizeOption}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.background.inputs.imageSize.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetBackgroundImageSize}
                />
            </ThemingFormGroupWrapper>

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUITheme.general.outerBackgroundRepeat}
                currentThemingPropertyValue={axnuiTheme.general.outerBackgroundRepeat}
                pageLevel={pageLevel}
            >
                <SelectFormGroup
                    controlId={"theming-layout-background-image-repeat-select-input"}
                    automationId={"theming-layout-background-image-repeat-select-input"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.background.inputs.imageRepeat.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.background.inputs.imageRepeat.label}
                    disabled={isDisabled}
                    isClearable={false}
                    isRequired={false}
                    isSearchable={false}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleBackgroundImageRepeatChange}
                    options={backgroundImageRepeatOptions}
                    selectedOption={selectedBackgroundImageRepeatOption}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.background.inputs.imageRepeat.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetBackgroundImageRepeat}
                />
            </ThemingFormGroupWrapper>

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUITheme.general.outerBackgroundAttachment}
                currentThemingPropertyValue={axnuiTheme.general.outerBackgroundAttachment}
                pageLevel={pageLevel}
            >
                <SelectFormGroup
                    controlId={"theming-layout-background-image-attachment-select-input"}
                    automationId={"theming-layout-background-image-attachment-select-input"}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.background.inputs.imageAttachment.label}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.background.inputs.imageAttachment.label}
                    disabled={isDisabled}
                    isClearable={false}
                    isRequired={false}
                    isSearchable={false}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleBackgroundImageAttachmentChange}
                    options={backgroundImageAttachmentOptions}
                    selectedOption={selectedBackgroundImageAttachmentOption}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.background.inputs.imageAttachment.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetBackgroundImageAttachment}
                />
            </ThemingFormGroupWrapper>
        </div>
    );
};